'use client';

import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fadeIn, staggerContainer } from '@/lib/animations';

export interface WarehousingHeroProps {
  location: string;
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

export function WarehousingHero({
  location,
  title,
  description,
  imageUrl,
  imageAlt,
}: WarehousingHeroProps) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden -mt-16">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-gradient-to-r from-brand-900/80 to-brand-700/70 z-10"
          aria-hidden="true"
        ></div>
        <img
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 py-24 md:py-32">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-4xl"
        >
          <motion.div variants={fadeIn('up', 0.2)}>
            <div className="w-fit mb-8 bg-white text-brand-600 rounded-full px-4 py-2 font-medium flex items-center gap-2">
              <MapPin size={16} />
              {location}
            </div>
          </motion.div>

          <motion.div
            variants={fadeIn('up', 0.3)}
            className="bg-[#1a325b] p-4 rounded-lg bg-opacity-80 md:p-10"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 text-shadow-lg">
              {title}
            </h1>

            <p className="text-xl md:text-2xl text-gray-100 mb-8 max-w-4xl leading-relaxed text-shadow-lg">
              {description}
            </p>

            <motion.div
              variants={fadeIn('up', 0.5)}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                asChild
                className="bg-white text-brand-600 hover:bg-gray-100 px-8 py-6 text-lg w-full md:w-auto"
              >
                <a href="#contact">Get Started</a>
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Wave decoration */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full">
          <path
            fill="#ffffff"
            fillOpacity="1"
            d="M0,96L80,85.3C160,75,320,53,480,53.3C640,53,800,75,960,80C1120,85,1280,75,1360,69.3L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"
          ></path>
        </svg>
      </div>
    </section>
  );
}

